<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import AgentProgress from './AgentProgress.vue';
import InteractiveAgentAvatar from './InteractiveAgentAvatar.vue';
import Naming from './steps/Naming.vue';
import Sequencer from './Sequencer.vue';
import HumanEscalation from './steps/HumanEscalation.vue';
import Task from './steps/Task.vue';
import Channel from './steps/Channel.vue';
import factory from './steps/channels/channel-wizard-factory.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
const NewChannelFormView = factory.create();

export default {
  components: {
    InteractiveAgentAvatar,
    AgentProgress,
    NextButton,
    Naming,
    Sequencer,
    HumanEscalation,
    Task,
    Channel,
    NewChannelFormView,
  },
  data() {
    return {
      totalSteps: 5,
    };
  },
  computed: {
    ...mapGetters({
      agentUiFlags: 'assistants/getUIFlags',
    }),
    ...mapState('assistantWizard', ['step', 'avatar', 'task', 'channel']),
  },
  beforeMount() {
    this.clear();
    this.fetchProcedureTemplates();
  },
  methods: {
    ...mapActions('assistants', ['generateAvatar']),
    ...mapActions('assistantWizard', [
      'clear',
      'next',
      'completeWizard',
      'updateAvatar',
      'fetchProcedureTemplates',
    ]),
    async finish() {
      if (this.agentUiFlags.isCreating) {
        return;
      }

      try {
        await this.completeWizard();

        this.$router.push({
          name: 'settings_assistant_list',
          params: {},
        });
      } catch (e) {
        // Error will be set in state for the forms to handle
        if (e.name === 'ValidationError') return;

        useAlert('Something went wrong');
      }
    },
    showInstructions() {
      return this.step === 0;
    },
    started() {
      return this.step > 0;
    },
    showNaming() {
      return this.step === 1;
    },
    showTask() {
      return this.step === 2;
    },
    showHumanEscalation() {
      return this.step === 3;
    },
    showChannel() {
      return this.step === 4;
    },
    showChannelConfig() {
      return this.step === 5;
    },
    configuring() {
      return this.step < this.totalSteps;
    },
    pastIntro() {
      return this.step > 0;
    },
    async onGenerateAvatar() {
      const generatedUrl = await this.generateAvatar({
        description: this.task,
      });
      this.updateAvatar(generatedUrl);
    },
  },
};
</script>

<template>
  <section
    class="flex flex-1 w-full bg-white dark:bg-slate-900 justify-center flex-col px-6"
  >
    <section
      v-show="showInstructions()"
      class="flex flex-col items-center py-8"
    >
      <section class="flex-1 flex items-center flex-col">
        <h1 class="text-2xl text-slate-900 dark:text-slate-50">
          {{ $t('ASSISTANT_MGMT.WIZARD.HELLO_LABEL') }}
        </h1>
        <p class="my-6 text-center">
          {{ $t('ASSISTANT_MGMT.WIZARD.AGENT_DESCRIPTION') }}
        </p>
        <img
          src="~dashboard/assets/images/rudy-avatar.png"
          alt="Rookoo-logo"
          class="border-slate-100 border-r rounded-full my-6"
          height="160px"
          width="160px"
        />
      </section>
      <section class="flex-1 mt-4">
        <NextButton
          icon="i-lucide-circle-plus"
          md
          :label="$t('ASSISTANT_MGMT.WIZARD.CREATE_PERSONA')"
          @click="next"
        />
      </section>
    </section>
    <section v-show="started()" class="flex flex-col items-center py-8">
      <section class="flex">
        <InteractiveAgentAvatar
          :loading="agentUiFlags.isGeneratingAvatar"
          :src="avatar"
          @generate="onGenerateAvatar"
        />
      </section>
      <section class="flex flex-col flex-1 items-center">
        <Naming v-if="showNaming()" />
        <Task v-if="showTask()" />
        <HumanEscalation v-if="showHumanEscalation()" />
        <Channel v-if="showChannel()" />
        <NewChannelFormView
          v-if="showChannelConfig()"
          :channel_name="channel"
        />
        <AgentProgress :total-steps="totalSteps" />
        <Sequencer :total-steps="totalSteps" @finish="finish" />
      </section>
    </section>
  </section>
</template>
